import { useState, useEffect } from "react";
import { FaArrowDown, FaArrowUp, FaTriangleExclamation, FaWrench, FaBoxOpen } from "react-icons/fa6";
import { LogisticMovementService } from "../../../../../services/LogisticMovementService";
import { LogisticItemService } from "../../../../../services/LogisticItemService";
import { ModalWraperLogistic } from "../../monitoring/create/wraper";

const TYPE_OPTIONS = [
  { value: "stock_in", label: "Barang Masuk", desc: "Stok bertambah (Pengadaan/Donasi)", icon: FaArrowDown, color: "text-emerald-400", activeBg: "border-emerald-500 bg-emerald-500/10" },
  { value: "stock_out", label: "Barang Keluar", desc: "Stok berkurang (Dipakai Teknisi)", icon: FaArrowUp, color: "text-blue-400", activeBg: "border-blue-500 bg-blue-500/10" },
  { value: "damaged", label: "Barang Rusak", desc: "Stok berkurang (Laporan Kerusakan)", icon: FaTriangleExclamation, color: "text-orange-400", activeBg: "border-orange-500 bg-orange-500/10" },
  { value: "adjustment", label: "Penyesuaian", desc: "Koreksi stok manual oleh Admin", icon: FaWrench, color: "text-amber-400", activeBg: "border-amber-500 bg-amber-500/10" },
];

export const CreateNewMovementModal = ({ refresh, openModal, setOpenModal }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [items, setItems] = useState([]);

  const [formData, setFormData] = useState({
    item_id: "",
    type: "stock_in",
    quantity: "",
    notes: "",
  });

  useEffect(() => {
    if (openModal) {
      LogisticItemService.get({ params: {} })
        .then((res) => setItems(res?.data?.data ?? []))
        .catch((err) => console.error(err));
    }
  }, [openModal]);

  const closeModal = () => {
    setOpenModal(false);
    setFormData({ item_id: "", type: "stock_in", quantity: "", notes: "" });
    setErrors({});
    setLoading(false);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.item_id) newErrors.item_id = "Barang harus dipilih";
    if (!formData.quantity || parseInt(formData.quantity) <= 0) newErrors.quantity = "Jumlah harus lebih dari 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      await LogisticMovementService.store({
        data: {
          item_id: formData.item_id,
          type: formData.type,
          quantity: parseInt(formData.quantity),
          notes: formData.notes,
        },
      });
      if (refresh) refresh();
      closeModal();
    } catch (err) {
      const serverErrors = err?.response?.data;
      if (typeof serverErrors === "object") {
        const formatted = {};
        for (const [key, value] of Object.entries(serverErrors)) {
          formatted[key] = Array.isArray(value) ? value[0] : value;
        }
        setErrors(formatted);
      } else if (serverErrors?.message) {
        setErrors({ general: serverErrors.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedItem = items.find((i) => i.id === formData.item_id);

  return (
    <ModalWraperLogistic isOpen={openModal} setIsOpen={setOpenModal} title="Catat Pergerakan Stok">
      <form onSubmit={handleSubmit} className="space-y-5">
        {errors.general && (
          <div className="px-4 py-3 rounded-xl text-sm text-red-400 border border-red-500/20 bg-red-500/5">{errors.general}</div>
        )}

        {/* Pilih Barang */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Pilih Barang <span className="text-red-400">*</span></label>
          <select
            value={formData.item_id}
            onChange={(e) => setFormData((p) => ({ ...p, item_id: e.target.value }))}
            className={`w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border text-gray-100 outline-none transition-all focus:ring-1 appearance-none cursor-pointer ${errors.item_id ? "border-red-500/50" : "border-slate-600/40 focus:border-cyan-500/50 focus:ring-cyan-500/20"}`}
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
          >
            <option value="">-- Pilih Barang --</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} {item.type ? `(${item.type})` : ""} [Sisa: {item.unused_stock} {item.unit}]
              </option>
            ))}
          </select>
          {errors.item_id && <p className="text-xs text-red-400 mt-1.5">{errors.item_id}</p>}
        </div>

        {/* Tipe Pergerakan */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Tipe Pergerakan <span className="text-red-400">*</span></label>
          <div className="grid grid-cols-2 gap-3">
            {TYPE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = formData.type === opt.value;
              return (
                <label key={opt.value} className={`cursor-pointer rounded-xl border px-4 py-3 transition-all ${isActive ? `${opt.activeBg} border-opacity-50` : "border-slate-700 hover:bg-slate-800/50"}`}>
                  <input type="radio" name="movement_type" value={opt.value} checked={isActive} onChange={(e) => setFormData((p) => ({ ...p, type: e.target.value }))} className="sr-only" />
                  <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-2 w-full justify-between">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? "border-current" : "border-slate-500"}`}>
                        {isActive && <div className="w-2.5 h-2.5 rounded-full bg-current" />}
                      </div>
                      <Icon className={`text-lg ${isActive ? opt.color : "text-slate-500"}`} />
                    </div>
                    <span className={`text-xs font-bold ${isActive ? opt.color : "text-slate-300"}`}>{opt.label}</span>
                    <span className="text-[10px] text-slate-500 leading-tight">{opt.desc}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Jumlah */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
            Jumlah {selectedItem ? `(${selectedItem.unit})` : ""} <span className="text-red-400">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={formData.quantity}
            onChange={(e) => setFormData((p) => ({ ...p, quantity: e.target.value }))}
            placeholder="Masukkan jumlah"
            className={`w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border text-gray-100 placeholder-slate-500 outline-none transition-all focus:ring-1 ${errors.quantity ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/20" : "border-slate-600/40 focus:border-cyan-500/50 focus:ring-cyan-500/20"}`}
          />
          {errors.quantity && <p className="text-xs text-red-400 mt-1.5">{errors.quantity}</p>}
        </div>

        {/* Catatan */}
        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Catatan / Keterangan</label>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
            placeholder="Contoh: Digunakan untuk PSB pelanggan Ahmad di ODP-012"
            className="w-full px-4 py-3 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all resize-none"
          />
        </div>

        {/* Info Stok Warning */}
        {selectedItem && formData.quantity && parseInt(formData.quantity) > selectedItem.unused_stock && (formData.type === "stock_out" || formData.type === "damaged") && (
          <div className="px-4 py-3 rounded-xl text-sm text-red-400 border border-red-500/20 bg-red-500/5 flex items-center gap-2">
            <FaTriangleExclamation className="flex-shrink-0" />
            <span>Jumlah melebihi stok tersedia (<strong>{selectedItem.unused_stock} {selectedItem.unit}</strong>). Transaksi akan ditolak sistem.</span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={closeModal} className="flex-1 py-3 rounded-xl border border-slate-700 text-slate-300 font-medium transition-colors hover:bg-slate-800">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-colors">
            {loading ? <i className="fa-solid fa-spinner fa-spin" /> : <FaBoxOpen className="text-sm" />}
            Simpan Pergerakan
          </button>
        </div>
      </form>
    </ModalWraperLogistic>
  );
};